import { takeEvery, fork, put, ForkEffect, PutEffect, CallEffect } from 'redux-saga/effects'
import { SSO_LOGIN, SSO_HANDLE_CALLBACK } from '../../actions/SSOLoginActions/ssoLoginAction'
import { ssoLoginFail } from '../../actions/SSOLoginActions/ssoLoginAction'
import { loginObtainedToken } from '../../actions/loginAction'
import { addLocalStorageValue } from '../../services/localStorageService'
import {
  LOCAL_TOKEN_KEY,
  LOCAL_REFRESH_KEY,
  SSO_ACCESS_TOKEN_KEY,
  SSO_REFRESH_TOKEN_KEY,
} from '../../_constants/variables'
import { analyticsTracker } from '../../services/analytics/analyticsTrackerService'
import { GAEventNames } from '../../domain/models/commons/GoogleAnalytics'
import text from '../../_constants/text'
import { popCookie } from '../../utils/cookieUtils'

const SSO_URL = process.env.REACT_APP_SSO_REDIRECT_URL

/**
 * Handle SSO login initiation - redirect user to the SSO provider
 */
export function* ssoLogin(): Generator<PutEffect, void, unknown> {
  try {
    // Clear previous callback processing state
    sessionStorage.removeItem('sso-callback-processed')
    if (!SSO_URL) {
      console.error('SSO configuration error: missing redirect URL')
      yield put(ssoLoginFail('SSO service is currently unavailable'))
      return
    }
    window.location.href = SSO_URL
  } catch (error) {
    console.error('SSO login error:', error)
    yield put(ssoLoginFail(text.UNEXPECTED_ERROR))
  }
}

/**
 * Handle SSO callback - processes JWT tokens from cookies set by backend
 * Stores tokens and dispatches loginObtainedToken action, similar to regular login flow
 */
export function* handleSSOCallback(): Generator<PutEffect | CallEffect, void, unknown> {
  try {
    // Prevent duplicate processing (handles React StrictMode double-execution)
    const processKey = `sso-callback-${Date.now()}`
    const alreadyProcessed = sessionStorage.getItem('sso-callback-processed')
    if (alreadyProcessed) {
      return
    }
    const accessToken = popCookie(SSO_ACCESS_TOKEN_KEY)
    const refreshToken = popCookie(SSO_REFRESH_TOKEN_KEY)
    if (!accessToken || !refreshToken) {
      console.error('SSO callback error: missing tokens in cookies')
      yield put(ssoLoginFail(text.UNEXPECTED_ERROR))
      return
    }
    sessionStorage.setItem('sso-callback-processed', processKey)
    addLocalStorageValue(LOCAL_TOKEN_KEY, accessToken)
    addLocalStorageValue(LOCAL_REFRESH_KEY, refreshToken)
    const tokenData = { type: 'Bearer', token: accessToken, refreshToken: refreshToken }
    yield put(loginObtainedToken(tokenData))
    analyticsTracker().track(GAEventNames.LOGIN, {})
  } catch (error) {
    console.error('SSO callback error:', error)
    sessionStorage.removeItem('sso-callback-processed')
    yield put(ssoLoginFail(text.UNEXPECTED_ERROR))
  }
}

function* watchSSOLogin(): Generator<ForkEffect<never>, void, unknown> {
  yield takeEvery(SSO_LOGIN, ssoLogin)
}

function* watchSSOCallback(): Generator<ForkEffect<never>, void, unknown> {
  yield takeEvery(SSO_HANDLE_CALLBACK, handleSSOCallback)
}

const ssoLoginSagas = [fork(watchSSOLogin), fork(watchSSOCallback)]

export default ssoLoginSagas
